import * as React from 'react';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate, MenuItemLink } from 'react-admin';
import { List, ListItem, Collapse, ListItemText, ListItemIcon } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { AppState } from '../types';
import grid from '../grid';

interface SubMenuProps {
    dense: boolean;
    onMenuClick: () => void;
}

interface SubMenuItem {
    to: string;
    primaryText: string;
    leftIcon?: React.ReactElement;
}

interface SubMenuCategoryProps {
    title: string;
    items: SubMenuItem[];
    dense: boolean;
    onMenuClick: () => void;
    sidebarOpen: boolean;
    translate: (key: string, options?: any) => string;
}

const useStyles = makeStyles(theme => ({
    categoryItem: {
        borderRadius: 12,
        margin: '6px 12px',
        padding: '8px 12px',
        transition: 'all 150ms ease',
        '&:hover': {
            backgroundColor: 'rgba(31, 77, 85, 0.08)',
        },
    },
    categoryText: {
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    categoryIcon: {
        minWidth: 40,
        color: '#5f6f73',
    },
    categoryChevron: {
        color: '#6b7477',
    },
    subItem: {
        margin: '4px 16px 6px 26px',
        borderRadius: 10,
        padding: '6px 12px',
    },
}));

const SubMenuCategory: FC<SubMenuCategoryProps> = ({ title, items, dense, onMenuClick, sidebarOpen, translate }) => {
    const [open, setOpen] = useState(false);
    const classes = useStyles();

    const handleClick = () => {
        setOpen(!open);
    };

    return (
        <List component="div" disablePadding>
            <ListItem button onClick={handleClick} className={classes.categoryItem}>
                <ListItemIcon className={classes.categoryIcon}>
                    <grid.icon />
                </ListItemIcon>
                <ListItemText primary={<span className="menu-label">{title}</span>} className={classes.categoryText} />
                {open ? <ExpandLess className={classes.categoryChevron} /> : <ExpandMore className={classes.categoryChevron} />}
            </ListItem>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {items.map((item, index) => (
                        <MenuItemLink
                            key={index}
                            to={item.to}
                            primaryText={<span className="menu-label">{item.primaryText}</span>}
                            leftIcon={item.leftIcon}
                            sidebarIsOpen={sidebarOpen}
                            dense={dense}
                            onClick={onMenuClick}
                            className={classes.subItem}
                        />
                    ))}
                </List>
            </Collapse>
        </List>
    );
};

const SubMenu: FC<SubMenuProps> = ({ onMenuClick, dense }) => {
    const translate = useTranslate();
    const sidebarOpen = useSelector((state: AppState) => state.admin.ui.sidebarOpen);

    const mortgageSubItems: SubMenuItem[] = [
        {
            to: '/addressAndCreditReport',
            primaryText: translate(`resources.addressAndCreditReport.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/generateBrokerReport',
            primaryText: translate(`resources.generateBrokerReport.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/pullCreditFile',
            primaryText: translate(`resources.pullCreditFile.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/UserMortgageData',
            primaryText: translate(`resources.UserMortgageData.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        }
    ].sort((a, b) => a.primaryText.localeCompare(b.primaryText));

    const marketingSubItems: SubMenuItem[] = [
        {
            to: '/applyPromoReferralCode',
            primaryText: translate(`resources.applyPromoReferralCode.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/createPromocode',
            primaryText: translate(`resources.createPromocode.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/promoCodeRecords',
            primaryText: translate(`resources.promocodeRecords.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/promoBannerConfig',
            primaryText: translate(`resources.promoBannerConfig.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        }
    ].sort((a, b) => a.primaryText.localeCompare(b.primaryText));

    const referralSubItems: SubMenuItem[] = [
        {
            to: '/referralConfig',
            primaryText: translate(`resources.referralConfig.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/referralUserTracking',
            primaryText: translate(`resources.referralUserTracking.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/referralFraudReview',
            primaryText: translate(`resources.referralFraudReview.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/brandHaircutConfig',
            primaryText: translate(`resources.brandHaircutConfig.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        }
    ];

    const giftCardsSubItems: SubMenuItem[] = [
        {
            to: '/giftCardBrands',
            primaryText: translate(`resources.giftCardBrands.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        }
    ];

    const deviceAuthSubItems: SubMenuItem[] = [
        {
            to: '/manageUserPasskeys',
            primaryText: translate(`resources.manageUserPasskeys.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/manageDeviceSignatures',
            primaryText: translate(`resources.manageDeviceSignatures.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        }
    ];

    const userActionSubItems: SubMenuItem[] = [
        {
            to: '/accountClosure',
            primaryText: translate(`resources.accountClosure.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/accountRestore',
            primaryText: translate(`resources.accountRestore.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/cancelGiftCard',
            primaryText: translate(`resources.cancelGiftCard.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/changeGiftCardState',
            primaryText: translate(`resources.changeGiftCardState.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/deleteVrpConsent',
            primaryText: translate(`resources.deleteVrpConsent.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/dupeAccountCheck',
            primaryText: translate(`resources.dupeAccountCheck.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/forcedLogoutUser',
            primaryText: translate(`resources.forcedLogoutUser.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/getVerificationLink',
            primaryText: translate(`resources.getVerificationLink.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/moveAccountRewards',
            primaryText: translate(`resources.moveAccountRewards.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/removePinForUser',
            primaryText: translate(`resources.removePinForUser.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/removeBiometricsForUser',
            primaryText: translate(`resources.removeBiometricsForUser.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/removeUserFromSuppressionList',
            primaryText: translate(`resources.removeUserFromSuppressionList.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/superChargedCalculator',
            primaryText: translate(`resources.superChargedCalculator.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/transferRewardAmount',
            primaryText: translate(`resources.transferRewardAmount.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/updateDirectDebit',
            primaryText: translate(`resources.updateDirectDebit.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/updatePayToLender',
            primaryText: translate(`resources.updatePayToLender.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/updatePendingTask',
            primaryText: translate(`resources.updatePendingTask.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/updateUser',
            primaryText: translate(`resources.updateUser.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/updateUserPendingAction',
            primaryText: translate(`resources.updateUserPendingAction.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/vdxDashboard',
            primaryText: translate(`resources.vdxDashboard.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/deleteUser',
            primaryText: translate(`resources.deleteUser.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        }
    ].sort((a, b) => {
        // Sort alphabetically, but keep deleteUser at the end
        if (a.to === '/deleteUser') return 1;
        if (b.to === '/deleteUser') return -1;
        return a.primaryText.localeCompare(b.primaryText);
    });

    const techSubItems: SubMenuItem[] = [
        {
            to: '/featureFlagDashboard',
            primaryText: translate(`resources.featureFlagDashboard.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/masterToUserWalletDashboard',
            primaryText: translate(`resources.masterToUserWalletDashboard.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/ppsPayee',
            primaryText: translate(`resources.ppsPayee.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/revolutRefund',
            primaryText: translate(`resources.revolutRefund.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/truelayerSyncFailureMonitor',
            primaryText: translate(`resources.truelayerSyncFailureMonitor.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        }
    ].sort((a, b) => a.primaryText.localeCompare(b.primaryText));

    const kpiSubItems: SubMenuItem[] = [
        {
            to: `/admin/grid`,
            primaryText: translate(`resources.grid.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: `/display-payments`,
            primaryText: translate(`resources.userPayments.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        }
    ].sort((a, b) => a.primaryText.localeCompare(b.primaryText));

    const archiveSubItems: SubMenuItem[] = [
        {
            to: '/dashboard',
            primaryText: translate(`resources.dashboard.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/homeValuation',
            primaryText: translate(`resources.homeValuation.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/giftCardDashboard',
            primaryText: translate(`resources.giftCardDashboard.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/pnNotifier',
            primaryText: translate(`resources.pnNotifier.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/admin/monitor-debit-algo-performance',
            primaryText: translate(`resources.debitCalculator.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/admin/inactive-users',
            primaryText: translate(`resources.userSessionInfo.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/masterToEwalletFailure',
            primaryText: translate(`resources.masterToEwalletFailure.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        },
        {
            to: '/redisCacheCleaner',
            primaryText: translate(`resources.redisCacheCleaner.name`, {
                smart_count: 2,
            }),
            leftIcon: <grid.icon />
        }
    ].sort((a, b) => a.primaryText.localeCompare(b.primaryText));

    return (
        <>
            <SubMenuCategory
                title={translate('resources.mortgage.name', { smart_count: 2 })}
                items={mortgageSubItems}
                dense={dense}
                onMenuClick={onMenuClick}
                sidebarOpen={sidebarOpen}
                translate={translate}
            />
            <SubMenuCategory
                title="Marketing"
                items={marketingSubItems}
                dense={dense}
                onMenuClick={onMenuClick}
                sidebarOpen={sidebarOpen}
                translate={translate}
            />
            <SubMenuCategory
                title="Referral"
                items={referralSubItems}
                dense={dense}
                onMenuClick={onMenuClick}
                sidebarOpen={sidebarOpen}
                translate={translate}
            />
            <SubMenuCategory
                title="Gift Cards"
                items={giftCardsSubItems}
                dense={dense}
                onMenuClick={onMenuClick}
                sidebarOpen={sidebarOpen}
                translate={translate}
            />
            <SubMenuCategory
                title="Device Authentication"
                items={deviceAuthSubItems}
                dense={dense}
                onMenuClick={onMenuClick}
                sidebarOpen={sidebarOpen}
                translate={translate}
            />
            <SubMenuCategory
                title="User Operations"
                items={userActionSubItems}
                dense={dense}
                onMenuClick={onMenuClick}
                sidebarOpen={sidebarOpen}
                translate={translate}
            />
            <SubMenuCategory
                title="Tech Operations"
                items={techSubItems}
                dense={dense}
                onMenuClick={onMenuClick}
                sidebarOpen={sidebarOpen}
                translate={translate}
            />
            <SubMenuCategory
                title="KPI"
                items={kpiSubItems}
                dense={dense}
                onMenuClick={onMenuClick}
                sidebarOpen={sidebarOpen}
                translate={translate}
            />
            <SubMenuCategory
                title="Archive Items"
                items={archiveSubItems}
                dense={dense}
                onMenuClick={onMenuClick}
                sidebarOpen={sidebarOpen}
                translate={translate}
            />
        </>
    );
};

export default SubMenu;
